import { createGlobalStyle, css } from 'styled-components';

import ProximaNovaBold from '../../assets/fonts/proxima-nova/ProximaNova-Bold.woff';
import ProximaNovaMedium from '../../assets/fonts/proxima-nova/ProximaNova-Medium.woff';
import ProximaNovaRegular from '../../assets/fonts/proxima-nova/ProximaNova-Regular.woff';
import ProximaNovaSemiBold from '../../assets/fonts/proxima-nova/ProximaNova-Semibold.woff';
import { theme } from '../../theme';
import { AlignmentEnum, ColorVariantEnum, FinePrintEnum, HeadingSizeEnum, IText, MarginSizeEnum } from './text.types';

export const DefaultGlobalFontCss = css`
	@font-face {
		font-family: 'ProximaNova-Regular';
		src: url(${ProximaNovaRegular});
	}
	@font-face {
		font-family: 'ProximaNova-Medium';
		src: url(${ProximaNovaMedium});
	}
	@font-face {
		font-family: 'ProximaNova-SemiBold';
		src: url(${ProximaNovaSemiBold});
	}
	@font-face {
		font-family: 'ProximaNova-Bold';
		src: url(${ProximaNovaBold});
	}
`;

export const DefaultGlobalFontStyle = createGlobalStyle`
	${DefaultGlobalFontCss};
`;

interface IGetColorFromColorVariantProp {
	colorVariant?: ColorVariantEnum | ColorVariantEnum[];
	fallback?: ColorVariantEnum;
	hover?: boolean;
}

export const getColorFromColorVariantProp = ({ colorVariant, fallback, hover }: IGetColorFromColorVariantProp) => {
	if (!colorVariant) {
		return fallback ? theme.colors.text[fallback] : 'initial';
	}

	const [color, hoverColor] = Array.isArray(colorVariant) ? colorVariant : [colorVariant, colorVariant];
	return hover ? theme.colors.text[hoverColor] || theme.colors.text[color] : theme.colors.text[color];
};

export const baseTextStyles = css<IText>`
	margin: 0;
	font-family: ${(props) => (props.weight ? theme.typography.fonts[props.weight] : theme.typography.fonts.medium)};
	color: ${(props) =>
		getColorFromColorVariantProp({
			colorVariant: props.colorVariant,
			fallback: ColorVariantEnum.DARK,
		})};
	margin-bottom: ${(props) => (props.margin ? props.margin : MarginSizeEnum.DEFAULT)};
	text-align: ${(props) => (props.alignment ? props.alignment : AlignmentEnum.LEFT)};
	text-decoration: ${(props) => props.underline && 'underline'};
	overflow: ${(props) => props.withEllipsis && 'hidden'};
	text-overflow: ${(props) => props.withEllipsis && 'ellipsis'};
	white-space: ${(props) => props.withEllipsis && 'nowrap'};
	&:hover {
		transition: color 0.2s ease-in-out;
		color: ${(props) =>
			getColorFromColorVariantProp({
				colorVariant: props.colorVariant,
				fallback: ColorVariantEnum.DARK,
				hover: true,
			})};
	}
`;

export const headingStyles = {
	[HeadingSizeEnum.SMALL]: css`
		font-size: 26px;
		line-height: 30px;
	`,
	[HeadingSizeEnum.MEDIUM]: css`
		font-size: 31px;
		line-height: 36px;
	`,
	[HeadingSizeEnum.LARGE]: css`
		font-size: 37px;
		line-height: 40px;
	`,
	[HeadingSizeEnum.EXTRA_LARGE]: css`
		font-size: 52px;
		line-height: 52px;
		letter-spacing: -0.1px;
		margin-bottom: 24px;
	`,
};

export const finePrintStyles = {
	[FinePrintEnum.CAPTION]: css`
		font-size: 13px;
		line-height: 18px;
	`,
	[FinePrintEnum.OVERLINE]: css`
		font-size: 11px;
		line-height: 16px;
		letter-spacing: 1.32px;
	`,
	[FinePrintEnum.TINY]: css`
		font-size: 11px;
		line-height: 16px;
	`,
};
